import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Star, Search, CheckCircle2, ShieldCheck, Loader2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/** Role options shown as pills */
const ROLE_OPTIONS = [
  { value: 'parent', label: 'Parent' },
  { value: 'student', label: 'Current student' },
  { value: 'alumni', label: 'Alumni' },
  { value: 'staff', label: 'Staff / teacher' },
  { value: 'prospective', label: 'Prospective parent' },
];

const YEAR_OPTIONS = [
  { value: '', label: 'Not applicable' },
  { value: 'primary', label: 'Primary (Y1–Y6)' },
  { value: 'secondary', label: 'Secondary (Y7–Y11)' },
  { value: 'sixth_form', label: 'Sixth Form (Y12–Y13)' },
];

/** Small tag pill */
const Pill = ({ active, onClick, children, testId }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testId}
    className={`px-3 py-1.5 rounded-full text-sm border transition ${
      active
        ? 'bg-gray-900 text-white border-gray-900'
        : 'bg-white text-gray-700 border-gray-300 hover:border-gray-500'
    }`}
  >
    {children}
  </button>
);

/** Interactive 5-star rating picker */
const StarPicker = ({ value, onChange }) => {
  const [hover, setHover] = useState(0);
  const shown = hover || value;
  return (
    <div
      className="inline-flex items-center gap-1"
      data-testid="rating-picker"
      onMouseLeave={() => setHover(0)}
    >
      {[1, 2, 3, 4, 5].map((i) => (
        <button
          key={i}
          type="button"
          onMouseEnter={() => setHover(i)}
          onClick={() => onChange(i)}
          data-testid={`rating-star-${i}`}
          className="focus:outline-none"
          aria-label={`${i} stars`}
        >
          <Star
            className={`w-8 h-8 transition ${
              i <= shown ? 'fill-amber-400 text-amber-400' : 'text-gray-300'
            }`}
          />
        </button>
      ))}
      {value > 0 && (
        <span className="ml-2 text-sm text-gray-600">{value} / 5</span>
      )}
    </div>
  );
};

/** Searchable school picker (autocomplete) */
const SchoolPicker = ({ onSelect, selected }) => {
  const [schools, setSchools] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/schools/list`);
        const data = await res.json();
        if (!cancelled) setSchools(data.schools || []);
      } catch {
        /* silent; UI handles empty list */
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Close dropdown on outside click
  useEffect(() => {
    const handle = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return schools.slice(0, 30);
    return schools
      .filter((s) =>
        (s.full_name || '').toLowerCase().includes(q) ||
        (s.name || '').toLowerCase().includes(q)
      )
      .slice(0, 30);
  }, [query, schools]);

  return (
    <div ref={wrapRef} className="relative" data-testid="school-picker">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
        <input
          type="text"
          value={selected ? selected.full_name : query}
          onChange={(e) => { setQuery(e.target.value); setOpen(true); if (selected) onSelect(null); }}
          onFocus={() => setOpen(true)}
          placeholder={loading ? 'Loading 122 schools…' : 'Search by school name…'}
          className="w-full pl-10 pr-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:border-gray-900"
          data-testid="school-search-input"
          disabled={loading}
        />
      </div>
      {open && !selected && (
        <div className="absolute z-20 left-0 right-0 mt-2 bg-white border border-gray-200 rounded-lg shadow-lg max-h-80 overflow-auto">
          {filtered.length === 0 ? (
            <p className="p-3 text-sm text-gray-500">No schools match.</p>
          ) : (
            filtered.map((s) => (
              <button
                key={s.id}
                type="button"
                onClick={() => { onSelect(s); setOpen(false); setQuery(''); }}
                data-testid={`school-option-${s.id}`}
                className="w-full text-left px-4 py-2.5 hover:bg-gray-100 border-b border-gray-100 last:border-0"
              >
                <p className="font-medium text-gray-900">{s.full_name}</p>
                {s.name && s.name !== s.full_name && (
                  <p className="text-xs text-gray-500">{s.name}</p>
                )}
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
};

const SubmitReviewPage = () => {
  const [school, setSchool] = useState(null);
  const [role, setRole] = useState('parent');
  const [yearContext, setYearContext] = useState('');
  const [rating, setRating] = useState(0);
  const [pros, setPros] = useState('');
  const [cons, setCons] = useState('');
  const [reviewText, setReviewText] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [honeypot, setHoneypot] = useState('');

  const [submitting, setSubmitting] = useState(false);
  const [submittedId, setSubmittedId] = useState(null);
  const [error, setError] = useState(null);

  const charsLeft = 5000 - reviewText.length;
  const reviewTooShort = reviewText.trim().length < 50;
  const canSubmit = school && rating >= 1 && !reviewTooShort && !submitting;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    setError(null);
    try {
      const payload = {
        school_id: school.id,
        role,
        year_context: yearContext || null,
        rating,
        pros: pros || null,
        cons: cons || null,
        review_text: reviewText,
        display_name: displayName || null,
        contact_email: contactEmail || null,
        website: honeypot, // honeypot, must be empty
      };
      const res = await fetch(`${API_URL}/api/public/reviews/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!res.ok) {
        const detail = data?.detail;
        throw new Error(
          typeof detail === 'string'
            ? detail
            : Array.isArray(detail)
              ? detail.map((d) => d.msg).join('; ')
              : 'Something went wrong. Please try again.'
        );
      }
      setSubmittedId(data.review_id);
    } catch (e2) {
      setError(e2.message || 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // Success screen
  if (submittedId) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-6" data-testid="review-success">
        <div className="max-w-lg w-full bg-white rounded-2xl shadow-lg p-10 text-center">
          <CheckCircle2 className="w-16 h-16 text-emerald-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 mb-3">Thank you for contributing</h1>
          <p className="text-gray-600 leading-relaxed">
            Your review about <strong>{school?.full_name}</strong> has been submitted and is now being
            reviewed by our moderation team. It will appear publicly on the school's page once approved —
            usually within 2 business days.
          </p>
          <p className="text-xs text-gray-400 mt-6">Reference: {submittedId.slice(0, 8)}</p>
          <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
            <button
              onClick={() => {
                setSubmittedId(null);
                setSchool(null);
                setRating(0);
                setPros('');
                setCons('');
                setReviewText('');
                setDisplayName('');
                setContactEmail('');
              }}
              className="px-5 py-2.5 border border-gray-900 text-gray-900 rounded-lg hover:bg-gray-900 hover:text-white transition"
              data-testid="submit-another-btn"
            >
              Submit another review
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Standalone header */}
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-3xl mx-auto px-6 py-5">
          <p className="text-sm text-gray-500">Switch Schools — Community Reviews</p>
          <h1 className="text-2xl font-bold text-gray-900 mt-1">Share your experience</h1>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-10">
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-8">
          <p className="text-gray-700 leading-relaxed mb-8">
            Your honest review helps parents across Malaysia make better school choices. Reviews are
            anonymous by default and moderated before publishing. <strong>No login required.</strong>
          </p>

          <form onSubmit={handleSubmit} className="space-y-8" data-testid="review-form">
            {/* Step 1 — School */}
            <div>
              <label className="block text-sm font-semibold text-gray-900 mb-2">
                1. Which school are you reviewing? <span className="text-red-500">*</span>
              </label>
              <SchoolPicker selected={school} onSelect={setSchool} />
              {school && (
                <p className="mt-2 text-sm text-gray-600" data-testid="selected-school">
                  Selected: <strong>{school.full_name}</strong>{' '}
                  <button
                    type="button"
                    onClick={() => setSchool(null)}
                    className="ml-2 text-xs text-gray-500 underline hover:text-gray-700"
                  >
                    change
                  </button>
                </p>
              )}
            </div>

            {/* Step 2 — Role */}
            <div>
              <label className="block text-sm font-semibold text-gray-900 mb-2">
                2. What's your relationship to the school? <span className="text-red-500">*</span>
              </label>
              <div className="flex flex-wrap gap-2">
                {ROLE_OPTIONS.map((r) => (
                  <Pill
                    key={r.value}
                    active={role === r.value}
                    onClick={() => setRole(r.value)}
                    testId={`role-option-${r.value}`}
                  >
                    {r.label}
                  </Pill>
                ))}
              </div>
            </div>

            {/* Year context */}
            <div>
              <label className="block text-sm font-semibold text-gray-900 mb-2">
                3. Which stage does your review relate to?
              </label>
              <div className="flex flex-wrap gap-2">
                {YEAR_OPTIONS.map((y) => (
                  <Pill
                    key={y.value || 'none'}
                    active={yearContext === y.value}
                    onClick={() => setYearContext(y.value)}
                    testId={`year-option-${y.value || 'none'}`}
                  >
                    {y.label}
                  </Pill>
                ))}
              </div>
            </div>

            {/* Rating */}
            <div>
              <label className="block text-sm font-semibold text-gray-900 mb-2">
                4. Overall rating <span className="text-red-500">*</span>
              </label>
              <StarPicker value={rating} onChange={setRating} />
            </div>

            {/* Review text */}
            <div>
              <label htmlFor="review_text" className="block text-sm font-semibold text-gray-900 mb-2">
                5. Your overall review <span className="text-red-500">*</span>
              </label>
              <textarea
                id="review_text"
                rows={6}
                value={reviewText}
                onChange={(e) => setReviewText(e.target.value.slice(0, 5000))}
                placeholder="Share your experience — what you liked, what could be better, anything other parents should know…"
                className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:border-gray-900 resize-none"
                data-testid="review-text-input"
              />
              <div className="flex items-center justify-between mt-1">
                <p className={`text-xs ${reviewTooShort ? 'text-amber-600' : 'text-gray-400'}`}>
                  {reviewTooShort
                    ? `${50 - reviewText.trim().length} more characters needed`
                    : 'Looks good'}
                </p>
                <p className="text-xs text-gray-400">{charsLeft} left</p>
              </div>
            </div>

            {/* Optional: pros and cons */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label htmlFor="pros" className="block text-sm font-semibold text-gray-900 mb-2">
                  What worked well? <span className="text-gray-400 font-normal">(optional)</span>
                </label>
                <textarea
                  id="pros"
                  rows={4}
                  value={pros}
                  onChange={(e) => setPros(e.target.value.slice(0, 1500))}
                  placeholder="e.g. caring teachers, strong pastoral care…"
                  className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:border-gray-900 resize-none"
                  data-testid="pros-input"
                />
              </div>
              <div>
                <label htmlFor="cons" className="block text-sm font-semibold text-gray-900 mb-2">
                  What didn't work? <span className="text-gray-400 font-normal">(optional)</span>
                </label>
                <textarea
                  id="cons"
                  rows={4}
                  value={cons}
                  onChange={(e) => setCons(e.target.value.slice(0, 1500))}
                  placeholder="e.g. limited sports options, high class sizes…"
                  className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:border-gray-900 resize-none"
                  data-testid="cons-input"
                />
              </div>
            </div>

            {/* Display name + contact */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label htmlFor="display_name" className="block text-sm font-semibold text-gray-900 mb-2">
                  Display name <span className="text-gray-400 font-normal">(optional)</span>
                </label>
                <input
                  id="display_name"
                  type="text"
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value.slice(0, 60))}
                  placeholder="Anonymous parent"
                  className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:border-gray-900"
                  data-testid="display-name-input"
                />
              </div>
              <div>
                <label htmlFor="contact_email" className="block text-sm font-semibold text-gray-900 mb-2">
                  Email <span className="text-gray-400 font-normal">(private — for moderation only)</span>
                </label>
                <input
                  id="contact_email"
                  type="email"
                  value={contactEmail}
                  onChange={(e) => setContactEmail(e.target.value.slice(0, 120))}
                  placeholder="you@example.com"
                  className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:border-gray-900"
                  data-testid="contact-email-input"
                />
              </div>
            </div>

            {/* Honeypot — hidden from humans */}
            <div style={{ position: 'absolute', left: '-5000px', top: 'auto' }} aria-hidden="true">
              <label>
                Website (do not fill)
                <input
                  type="text"
                  tabIndex="-1"
                  autoComplete="off"
                  value={honeypot}
                  onChange={(e) => setHoneypot(e.target.value)}
                />
              </label>
            </div>

            {error && (
              <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700" data-testid="submit-error">
                {error}
              </div>
            )}

            <div className="flex items-center justify-between gap-4 pt-4 border-t border-gray-100">
              <p className="text-xs text-gray-500 flex items-center gap-1">
                <ShieldCheck className="w-3.5 h-3.5" /> Reviews are moderated before publishing.
              </p>
              <button
                type="submit"
                disabled={!canSubmit}
                className="inline-flex items-center gap-2 px-6 py-3 rounded-lg bg-gray-900 text-white font-semibold hover:bg-black disabled:bg-gray-300 disabled:cursor-not-allowed transition"
                data-testid="submit-review-btn"
              >
                {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
                {submitting ? 'Submitting…' : 'Submit review'}
              </button>
            </div>
          </form>
        </div>

        <p className="text-xs text-center text-gray-400 mt-8">
          By submitting a review you confirm your comments are honest and factual. Switch Schools
          reserves the right to edit or remove reviews that violate our community guidelines.
        </p>
      </main>
    </div>
  );
};

export default SubmitReviewPage;
